import { useCustom } from '@refinedev/core';
import {
  Badge,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  InboxOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type {
  ErrorSpikeStatsOut,
  SegmentErrorStatsOut,
  SegmentPipelineStatsOut,
} from '@/api/generated/schemas';

type PipelineCount = keyof SegmentPipelineStatsOut;

// A segment is billed on two independent clocks — each has its own backlog.
const BILLING_CLOCKS: {
  key: string;
  label: string;
  hint: string;
  pending: PipelineCount;
  closed: PipelineCount;
}[] = [
  {
    key: 'user',
    label: 'Użytkownicy',
    hint: 'Rozliczenie po stronie użytkownika (processed_at)',
    pending: 'unprocessed',
    closed: 'processed_24h',
  },
  {
    key: 'publisher',
    label: 'Wydawcy',
    hint: 'Rozliczenie po stronie wydawcy (accounted_at), każdy settlement_status',
    pending: 'unaccounted',
    closed: 'accounted_24h',
  },
];

export const SegmentHealthCards = () => {
  const { query: pipelineQuery } = useCustom<SegmentPipelineStatsOut>({
    url: 'progress/segments/stats',
    method: 'get',
  });
  const { query: errorsQuery } = useCustom<SegmentErrorStatsOut>({
    url: 'progress/segment_errors/stats',
    method: 'get',
    config: { query: { days: 7 } },
  });
  const { query: spikeQuery } = useCustom<ErrorSpikeStatsOut>({
    url: 'progress/segment_errors/stats/spike',
    method: 'get',
  });

  const pipeline = pipelineQuery.data?.data;
  const errors = errorsQuery.data?.data;
  const spike = spikeQuery.data?.data;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={8}>
        <Card title="Pipeline segmentów" loading={pipelineQuery.isLoading}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Statistic
              title="Zsynchronizowane (24h)"
              value={pipeline?.synced_24h ?? 0}
              prefix={<SyncOutlined />}
            />
            <Row gutter={16}>
              {BILLING_CLOCKS.map((clock) => {
                const pending = pipeline?.[clock.pending] ?? 0;
                return (
                  <Col span={12} key={clock.key}>
                    <Tooltip title={clock.hint}>
                      <Typography.Text strong>{clock.label}</Typography.Text>
                    </Tooltip>
                    <Statistic
                      title="Oczekujące"
                      value={pending}
                      prefix={<InboxOutlined />}
                      valueStyle={{
                        fontSize: 20,
                        color: pending > 0 ? '#cf1322' : '#8c8c8c',
                      }}
                    />
                    <Statistic
                      title="Zamknięte (24h)"
                      value={pipeline?.[clock.closed] ?? 0}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ fontSize: 20, color: '#3f8600' }}
                    />
                  </Col>
                );
              })}
            </Row>
          </Space>
        </Card>
      </Col>

      <Col xs={24} md={8}>
        <Card
          title={`Błędy segmentów (${errors?.days ?? 7}d)`}
          loading={errorsQuery.isLoading}
        >
          <Statistic
            title="Łącznie"
            value={errors?.total ?? 0}
            prefix={<WarningOutlined />}
            valueStyle={{
              color: (errors?.total ?? 0) > 0 ? '#d46b08' : undefined,
            }}
          />
          {errors?.by_reason && Object.keys(errors.by_reason).length > 0 && (
            <Space wrap style={{ marginTop: 8 }}>
              {Object.entries(errors.by_reason).map(([reason, count]) => (
                <Tag key={reason} color="orange">
                  {reason}: {count}
                </Tag>
              ))}
            </Space>
          )}
        </Card>
      </Col>

      <Col xs={24} md={8}>
        <Card title="Spike błędów" loading={spikeQuery.isLoading}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Typography.Text type="secondary">
                Bieżąca godzina:
              </Typography.Text>
              <Typography.Text strong>
                {spike?.current_hour ?? 0}
              </Typography.Text>
            </Space>
            <Space>
              <Typography.Text type="secondary">
                Poprzednia godzina:
              </Typography.Text>
              <Typography.Text>{spike?.previous_hour ?? 0}</Typography.Text>
            </Space>
            {spike?.change_pct != null && (
              <Space>
                <Badge status={spike.change_pct > 0 ? 'error' : 'success'} />
                <Typography.Text
                  type={spike.change_pct > 0 ? 'danger' : 'success'}
                >
                  {spike.change_pct > 0 ? '+' : ''}
                  {spike.change_pct.toFixed(1)}% zmiana
                </Typography.Text>
              </Space>
            )}
          </Space>
        </Card>
      </Col>
    </Row>
  );
};
